import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'
import { formatRest, useRestTimer } from './useRestTimer'

/**
 * Dinlenme sayacı da App seviyesinde — mola sayacıyla aynı sebeple.
 * Set arasında başka bir sekmeye bakarsan sayaç durmamalı; daha önce
 * mola sayacında tam bu hata vardı.
 */
type RestCtx = ReturnType<typeof useRestTimer> & {
  soundOn: boolean
  setSoundOn: (v: boolean) => void
}

const Ctx = createContext<RestCtx | null>(null)

export function RestTimerProvider({ children }: { children: ReactNode }) {
  const [soundOn, setSoundOn] = useState(true)
  const rest = useRestTimer(soundOn)

  return (
    <Ctx.Provider value={{ ...rest, soundOn, setSoundOn }}>
      {children}
    </Ctx.Provider>
  )
}

export function useRest(): RestCtx {
  const c = useContext(Ctx)
  if (!c) throw new Error('useRest, RestTimerProvider içinde kullanılmalı')
  return c
}

/**
 * Ekranın altına yapışan ince şerit. Tam ekran kaplamıyor — mola sayacının
 * aksine dinlenme sırasında telefonu kullanmaya devam edeceksin (bir sonraki
 * hareketin anlatımına bakmak gibi). Ekranı kapatmak burada engel olurdu.
 */
export function RestBar() {
  const rest = useRest()

  /**
   * Şerit açıkken sayfaya ekstra alt boşluk ver.
   *
   * Şerit `position: fixed` ve telefonda alt gezinme çubuğunun hemen üstünde
   * duruyor. Sayfanın alt boşluğu yalnızca gezinme çubuğunu hesaba katıyordu,
   * yani dinlenme sayarken içeriğin son ~110 pikseli şeridin altında kalıyordu —
   * hem de tam işaretlediğin set ile bir sonraki setin olduğu yerde. Sayfayı
   * kaydırabiliyorsun ama kaydıracak yer kalmıyor.
   */
  useEffect(() => {
    const el = document.documentElement
    if (rest.running) el.setAttribute('data-resting', 'true')
    else el.removeAttribute('data-resting')
    return () => el.removeAttribute('data-resting')
  }, [rest.running])

  if (!rest.running) return null

  const urgent = rest.left <= 10

  return (
    <div className="restbar" role="status" aria-live="off">
      <div className="restbar-fill" style={{ width: `${rest.progress * 100}%` }} />
      <div className="restbar-body">
        <span className={urgent ? 'restbar-time urgent' : 'restbar-time'}>
          {formatRest(rest.left)}
        </span>
        <span className="restbar-label">{rest.label || 'dinlenme'}</span>
        <div className="restbar-actions">
          <button onClick={() => rest.add(30)}>+30 sn</button>
          <button onClick={() => rest.setSoundOn(!rest.soundOn)}
                  title={rest.soundOn ? 'Sesi kapat' : 'Sesi aç'}>
            {rest.soundOn ? '🔔' : '🔕'}
          </button>
          <button className="primary" onClick={rest.stop}>Hazırım</button>
        </div>
      </div>
    </div>
  )
}
